/**
 * SanctionsScreen — the bank's own sanctions screening, for Risk Management (Bill 2026-10-19).
 *
 * Bill: "Our OFAC sourcing should be the golden rule and the only reason we are checking what JHA returns is to know
 * what the nightly Core Director hits will be and flag any false positives."
 *
 * Five views:
 *   Reviews   — held screenings waiting for Risk to clear or confirm (SanctionsReviews, Bill 2026-11-02).
 *   Lists     — each government list: what it can do (Risk settings > Sanctions screening), how many parties are
 *               loaded, when it last loaded, and whether it is current. Reload on demand.
 *   Test      — screen a name exactly as account opening does (recorded as a Risk test, no application touched).
 *   Calibrate — evidence for the score settings: catch rate on variants of listed names, and how many ordinary
 *               names reach review.
 *   Forecast  — recent Core Director OFAC hits and how each was labelled against our lists.
 *
 * The settings themselves live on the Risk settings screen (one place to change them, one change log).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import {
  calibrate,
  loadForecast,
  loadSanctionsSources,
  refreshSanctionsLists,
  screenName,
  type CalibrationReport,
  type ForecastItem,
  type SanctionsScreenResult,
  type SanctionsSourcesView,
} from '@/services/sanctionsApi';
import { SanctionsReviews } from './SanctionsReviews';

type Tab = 'reviews' | 'lists' | 'test' | 'calibrate' | 'forecast';

// Bill 2026-11-02: plain labels, the same on the Sanctions Screening report and in Risk settings.
const ROLE_LABEL: Record<string, string> = { decide: 'Match or review', review: 'Holds for review', off: 'Off' };
const BAND_LABEL: Record<string, string> = { hit: 'Match', review: 'Possible match', 'cleared-by-detail': 'Cleared by date or country' };
const VERDICT_LABEL: Record<string, string> = {
  clear: 'Clear', review: 'Risk review', hit: 'OFAC hit', pending: 'Pending (a list is not current)',
};
const ASSESSMENT_LABEL: Record<string, string> = {
  'also-our-match': 'Same party our screening found',
  delisted: 'Expected false positive: delisted by Treasury',
  'not-our-match': 'Expected false positive: not a match under our rules',
  unaccounted: 'Not on our lists: sent to Risk',
};

/** The sanctions routes return UTC times without a zone suffix; read them as UTC so they show in local
 *  (Central) time. Before this, the 5:30 CT list load showed as "10:30 AM" (CHG-0080). */
export function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso.trim());
  const d = new Date(hasZone ? iso : `${iso.trim()}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function pillFor(v: string): string {
  if (v === 'clear' || v === 'decide') return 'pill pill--ok';
  if (v === 'hit') return 'pill pill--err';
  if (v === 'review' || v === 'pending') return 'pill pill--warn';
  return 'pill pill--muted';
}

export function SanctionsScreen(): JSX.Element {
  const [tab, setTab] = useState<Tab>('reviews');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [view, setView] = useState<SanctionsSourcesView | null>(null);
  const [loadingLists, setLoadingLists] = useState(true);

  const [name, setName] = useState('');
  const [nameType, setNameType] = useState<'Indv' | 'Org'>('Indv');
  const [birthDate, setBirthDate] = useState('');
  const [country, setCountry] = useState('');
  const [screening, setScreening] = useState<SanctionsScreenResult | null>(null);
  const [busy, setBusy] = useState(false);

  const [sample, setSample] = useState(300);
  const [report, setReport] = useState<{ report: CalibrationReport; elapsedMs: number; indexedNames: number } | null>(null);

  const [forecast, setForecast] = useState<ReadonlyArray<ForecastItem> | null>(null);

  const refreshLists = useCallback(async (): Promise<void> => {
    setLoadingLists(true);
    try {
      setView(await loadSanctionsSources());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the lists.');
    } finally {
      setLoadingLists(false);
    }
  }, []);

  useEffect(() => { void refreshLists(); }, [refreshLists]);

  useEffect(() => {
    if (tab !== 'forecast' || forecast !== null) return;
    loadForecast().then(setForecast).catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load the forecast.'));
  }, [tab, forecast]);

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'The request failed.'); } finally { setBusy(false); }
  };

  return (
    <>
      <Link className="back-link" to="/">← Home</Link>
      <section className="page-header">
        <h1 className="page-header__title">Sanctions screening</h1>
        <p className="page-header__subtitle">
          Account opening screens every name against the bank&apos;s own copy of the government sanctions lists, loaded
          daily from each government&apos;s own site. OFAC&apos;s lists are <strong>Match or review</strong>; the other lists
          <strong>Hold for review</strong>. Either way a close match holds the application until you clear or confirm it on
          the Reviews tab. Core Director&apos;s OFAC search still runs, so you can see in advance what its nightly report will
          show and why. What each list can do, and the score lines, are on{' '}
          <Link to="/risk-settings">Risk settings → Sanctions screening</Link>.
        </p>
      </section>

      <nav className="subnav" role="tablist" aria-label="Sanctions views">
        <button type="button" role="tab" aria-selected={tab === 'reviews'} onClick={() => setTab('reviews')}>Reviews</button>
        <button type="button" role="tab" aria-selected={tab === 'lists'} onClick={() => setTab('lists')}>Lists</button>
        <button type="button" role="tab" aria-selected={tab === 'test'} onClick={() => setTab('test')}>Test a name</button>
        <button type="button" role="tab" aria-selected={tab === 'calibrate'} onClick={() => setTab('calibrate')}>Calibrate</button>
        <button type="button" role="tab" aria-selected={tab === 'forecast'} onClick={() => setTab('forecast')}>Core Director forecast</button>
      </nav>

      {error && <section className="banner banner--err" role="alert">{error}</section>}
      {notice && <section className="banner banner--info" role="status">{notice}</section>}

      {tab === 'reviews' && <SanctionsReviews />}

      {tab === 'lists' && (
        <section className="card">
          <div className="card-header"><h2>Government lists</h2></div>
          {loadingLists || !view ? <p className="empty">Loading the lists…</p> : (
            <>
              <p className="form-hint">
                Decided by: <strong>{view.authority === 'pcb' ? 'the bank’s own lists' : 'Core Director’s OFAC search'}</strong>.
                Hit at {view.hitScore}, review at {view.reviewScore}. {view.indexedNames.toLocaleString()} listed names searchable
                (matcher {view.matcherVersion}). A list that has not loaded for {view.staleAfterHours} hours makes screenings
                &ldquo;pending&rdquo;, never &ldquo;clear&rdquo;.
              </p>
              <table className="table">
                <thead>
                  <tr><th>List</th><th>Can</th><th>Parties</th><th>Publisher date</th><th>Last loaded</th><th>Current</th></tr>
                </thead>
                <tbody>
                  {view.sources.map((s) => (
                    <tr key={s.id}>
                      <td><strong>{s.label}</strong><div className="form-hint">{s.publisher}</div></td>
                      <td><span className={pillFor(s.role)}>{ROLE_LABEL[s.role] ?? s.role}</span></td>
                      <td>{s.entries.toLocaleString()}</td>
                      <td>{s.publisherDataAsOf ? new Date(s.publisherDataAsOf).toLocaleDateString() : '—'}</td>
                      <td>{when(s.lastSuccessAt)}</td>
                      <td>{s.role === 'off' ? '—' : <span className={s.fresh ? 'pill pill--ok' : 'pill pill--err'}>{s.fresh ? 'Yes' : 'No'}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="action-row" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={busy}
                  onClick={() => void run(async () => {
                    await refreshSanctionsLists();
                    setNotice('Reload started. It takes a few minutes; refresh this view to see the new load times.');
                  })}
                >
                  Reload every list now
                </button>
                <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void refreshLists()}>Refresh this view</button>
              </div>
              {view.runs.length > 0 && (
                <>
                  <h3 style={{ marginTop: 16 }}>Recent loads</h3>
                  <table className="table">
                    <thead><tr><th>List</th><th>Result</th><th>Started</th><th>Parties</th><th>Before</th><th>Note</th></tr></thead>
                    <tbody>
                      {view.runs.slice(0, 24).map((r, i) => (
                        <tr key={`${r.sourceId}-${r.startedAt}-${i}`}>
                          <td>{r.sourceId}</td>
                          <td><span className={r.status === 'success' ? 'pill pill--ok' : r.status === 'in-progress' ? 'pill pill--info' : 'pill pill--err'}>{r.status}</span></td>
                          <td>{when(r.startedAt)}</td>
                          <td>{r.entryCount ?? '—'}</td>
                          <td>{r.previousEntryCount ?? '—'}</td>
                          <td>{r.error ?? r.triggerSource ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </section>
      )}

      {tab === 'test' && (
        <section className="card">
          <div className="card-header"><h2>Test a name</h2></div>
          <p className="form-hint">
            Runs the same screening account opening runs, including Core Director&apos;s OFAC search, and records it as a Risk
            test. No application or customer record is touched. Use listed names or made-up names, not customers.
          </p>
          <label className="form-field">
            <span className="form-field__label">Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
          </label>
          <label className="form-field">
            <span className="form-field__label">Type</span>
            <select value={nameType} onChange={(e) => setNameType(e.target.value === 'Org' ? 'Org' : 'Indv')}>
              <option value="Indv">Person</option>
              <option value="Org">Business or organization</option>
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Date of birth (optional, YYYY-MM-DD)</span>
            <input type="text" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} maxLength={10} />
          </label>
          <label className="form-field">
            <span className="form-field__label">Country (optional)</span>
            <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} maxLength={60} />
          </label>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || name.trim().length < 2}
            onClick={() => void run(async () => { setScreening(await screenName(name.trim(), nameType, birthDate.trim(), country.trim())); })}
          >
            {busy ? 'Screening…' : 'Screen'}
          </button>

          {screening && (
            <div style={{ marginTop: 16 }}>
              <p>
                <span className={pillFor(screening.verdict)}>{VERDICT_LABEL[screening.verdict] ?? screening.verdict}</span>{' '}
                {screening.summary}
              </p>
              {screening.candidates.length > 0 && (
                <table className="table">
                  <thead><tr><th>Score</th><th>Result</th><th>Matched name</th><th>Listed party</th><th>List</th><th>Born / countries</th></tr></thead>
                  <tbody>
                    {screening.candidates.map((c) => (
                      <tr key={`${c.sourceId}-${c.sourceEntryId}`}>
                        <td>{c.score}</td>
                        <td><span className={pillFor(c.band)}>{BAND_LABEL[c.band] ?? c.band}</span>{c.weakAlias ? ' (weak alias)' : ''}</td>
                        <td>{c.matchedName}</td>
                        <td>{c.primaryName}<div className="form-hint">{c.programs.join(', ')}</div></td>
                        <td>{c.sourceLabel} <span className={pillFor(c.role)}>{ROLE_LABEL[c.role] ?? c.role}</span></td>
                        <td>
                          {c.birthDates.slice(0, 3).join(', ') || '—'}{c.dobComparison ? ` (${c.dobComparison})` : ''}
                          <div className="form-hint">{c.countries.slice(0, 3).join(', ')}{c.countryComparison ? ` (${c.countryComparison})` : ''}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <h3 style={{ marginTop: 12 }}>Core Director&apos;s OFAC search</h3>
              {screening.forecast.length === 0 ? (
                <p className="form-hint">
                  {screening.forecastPath === 'off' ? 'Not run (turned off in Risk settings).'
                    : screening.forecastPath === 'search-failed' ? 'The search could not be run; our screening stands.'
                    : 'No hits: the nightly report should not list this name.'}
                </p>
              ) : (
                <table className="table">
                  <thead><tr><th>Core Director hit</th><th>Our label</th><th>Why</th></tr></thead>
                  <tbody>
                    {screening.forecast.map((f) => (
                      <tr key={f.aliasId}>
                        <td>{f.aliasName}<div className="form-hint">Alias #{f.aliasId}{f.entityId ? ` · entity ${f.entityId}` : ''}</div></td>
                        <td>{ASSESSMENT_LABEL[f.assessment] ?? f.assessment}</td>
                        <td>{f.explanation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>
      )}

      {tab === 'calibrate' && (
        <section className="card">
          <div className="card-header"><h2>Calibrate the score lines</h2></div>
          <p className="form-hint">
            Takes listed parties from every loaded list and screens realistic variants of their names (the exact name, a
            missing middle name, reversed order, one typo, two words written together, a transliterated spelling) at the
            current score lines. Then screens 2,500 ordinary U.S. names (common first names × common surnames, made up)
            to show how many would go to review. Takes up to a minute or two.
          </p>
          <label className="form-field">
            <span className="form-field__label">Listed parties to sample (50-1000)</span>
            <input type="text" value={String(sample)} onChange={(e) => setSample(Math.max(50, Math.min(1000, Number.parseInt(e.target.value, 10) || 300)))} />
          </label>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void run(async () => { setReport(await calibrate(sample)); })}>
            {busy ? 'Calibrating…' : 'Run calibration'}
          </button>
          {report && (
            <div style={{ marginTop: 16 }}>
              <p className="form-hint">
                Hit at {report.report.hitScore}, review at {report.report.reviewScore}; {report.report.entriesSampled} listed parties
                sampled from {report.indexedNames.toLocaleString()} names in {(report.elapsedMs / 1000).toFixed(1)} s.
              </p>
              <table className="table">
                <thead><tr><th>Variant of a listed name</th><th>Caught</th><th>Rate</th></tr></thead>
                <tbody>
                  {report.report.catchRates.map((v) => (
                    <tr key={v.variant}><td>{v.variant}</td><td>{v.caught} of {v.tried}</td><td>{v.catchRate}%</td></tr>
                  ))}
                </tbody>
              </table>
              <p style={{ marginTop: 12 }}>
                <strong>Ordinary names reaching review: {report.report.ordinaryNamesFlagged} of {report.report.ordinaryNamesTried} ({report.report.falseHitRate}%).</strong>{' '}
                {report.report.ordinaryNamesFlaggedOnExactName} of those because a listed party carries the exact same name.
              </p>
              {report.report.misses.length > 0 && (
                <>
                  <h3>Variants not caught</h3>
                  <ul>{report.report.misses.slice(0, 30).map((m, i) => <li key={i}>{m.variant}: &ldquo;{m.query}&rdquo; for {m.listedName} ({m.sourceId})</li>)}</ul>
                </>
              )}
              {report.report.flaggedExamples.length > 0 && (
                <>
                  <h3>Examples of ordinary names that reach review</h3>
                  <ul>{report.report.flaggedExamples.slice(0, 30).map((f, i) => <li key={i}>{f}</li>)}</ul>
                </>
              )}
            </div>
          )}
        </section>
      )}

      {tab === 'forecast' && (
        <section className="card">
          <div className="card-header"><h2>Core Director forecast (last 14 days)</h2></div>
          <p className="form-hint">
            Screenings where Core Director&apos;s OFAC search returned hits, and how each hit was labelled. &ldquo;Expected false
            positive&rdquo; hits will appear on the nightly report and can be dismissed there with this explanation.
          </p>
          {forecast === null ? <p className="empty">Loading…</p> : forecast.length === 0 ? <p className="empty">No Core Director hits in the last 14 days.</p> : (
            <table className="table">
              <thead><tr><th>When</th><th>Name</th><th>Our result</th><th>Core Director hits</th></tr></thead>
              <tbody>
                {forecast.map((f) => (
                  <tr key={f.screeningId}>
                    <td>{when(f.screenedAt)}<div className="form-hint">{f.context}</div></td>
                    <td>{f.name}</td>
                    <td><span className={pillFor(f.verdict)}>{VERDICT_LABEL[f.verdict] ?? f.verdict}</span></td>
                    <td>
                      {(f.forecast?.hits ?? []).map((h) => (
                        <div key={h.aliasId}>{h.aliasName}: {ASSESSMENT_LABEL[h.assessment] ?? h.assessment}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </>
  );
}
